"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SUPPORT_RECIPIENTS } from "@/lib/email-recipients";
import { ChevronDown, Users } from "lucide-react";

interface CcSelectorProps {
  value: string[];
  onValueChange: (emails: string[]) => void;
  excludeEmail?: string; // Exclude the "To" recipient
}

export function CcSelector({ value, onValueChange, excludeEmail }: CcSelectorProps) {
  const [open, setOpen] = useState(false);

  // Filter out the main recipient from CC options
  const availableRecipients = SUPPORT_RECIPIENTS.filter(
    (r) => r.email !== excludeEmail
  );

  const handleToggle = (email: string) => {
    if (value.includes(email)) {
      onValueChange(value.filter((e) => e !== email));
    } else {
      onValueChange([...value, email]);
    }
  };

  const selectedNames = value
    .map((email) => SUPPORT_RECIPIENTS.find((r) => r.email === email)?.name || email)
    .join(", ");

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">CC (optional)</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            <span className="flex items-center gap-2 truncate">
              <Users className="h-4 w-4 shrink-0" />
              {value.length > 0 ? (
                <span className="truncate">{selectedNames}</span>
              ) : (
                <span className="text-muted-foreground">Add CC recipients...</span>
              )}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <div className="max-h-[300px] overflow-auto p-2">
            {availableRecipients.length === 0 ? (
              <p className="text-sm text-muted-foreground p-2">
                No additional recipients available
              </p>
            ) : (
              availableRecipients.map((recipient) => (
                <div
                  key={recipient.id}
                  className="flex items-start gap-3 p-2 rounded-md hover:bg-accent cursor-pointer"
                  onClick={() => handleToggle(recipient.email)}
                >
                  <Checkbox
                    checked={value.includes(recipient.email)}
                    onCheckedChange={() => handleToggle(recipient.email)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{recipient.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {recipient.email}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
      {value.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {value.length} recipient{value.length > 1 ? "s" : ""} selected
        </p>
      )}
    </div>
  );
}
